"use client";

import { useQuery } from "@tanstack/react-query";
import { useDebounceFn } from "ahooks";
import { AnimatePresence, motion } from "framer-motion";
import { Loader2 } from "lucide-react";
import Image from "next/image";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import searchIcon from "@/assets/images/common/search-icon-header.png";
import DeleteIcon from "@/assets/svg/del.svg";
import { Input } from "@/components/ui/input";
import { useSearchHistory } from "@/hooks/useSearchHistory";
import { cn } from "@/lib/utils";
import { useLocaleRouter } from "@/lib/utils.client";
import { useORPC } from "@/providers/orpc-provider";
import { useTracking } from "@/track";
import { trackSearch } from "@/track/gtm";
interface SuggestionItem {
  id: string;
  label: string;
}
interface HeaderSearchComponentProps {
  className?: string;
}
export const SearchComponent = ({
  className
}: HeaderSearchComponentProps) => {
  const {
    t
  } = useTranslation();
  const {
    track,
    TrackingEventType
  } = useTracking();
  const [isFocused, setIsFocused] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const [predictionQuery, setPredictionQuery] = useState("");
  const {
    searchHistory,
    addHistory,
    clearHistory
  } = useSearchHistory();
  const [localSearchHistory, setLocalSearchHistory] = useState(searchHistory);
  const [currentPlaceholder, setCurrentPlaceholder] = useState("");
  const [_placeholderIndex, setPlaceholderIndex] = useState(0);
  const searchContainerRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const router = useLocaleRouter();
  const orpc = useORPC();

  // 同步 searchHistory 到本地状态
  useEffect(() => {
    setLocalSearchHistory(searchHistory);
  }, [searchHistory]);

  // 添加 localStorage 变化监听
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === "searchHistory") {
        try {
          const newHistory = e.newValue ? JSON.parse(e.newValue) : [];
          if (Array.isArray(newHistory)) {
            setLocalSearchHistory(newHistory);
          }
        } catch (error) {
          console.error("解析搜索历史失败:", error);
        }
      }
    };

    // 监听其他标签页的 storage 事件
    window.addEventListener("storage", handleStorageChange);

    // 监听当前标签页的 storage 变化
    const originalSetItem = localStorage.setItem;
    localStorage.setItem = function (key, value) {
      const event = new StorageEvent("storage", {
        key: key,
        newValue: value,
        oldValue: localStorage.getItem(key),
        storageArea: localStorage
      });
      originalSetItem.apply(this, [key, value]);
      window.dispatchEvent(event);
    };
    return () => {
      window.removeEventListener("storage", handleStorageChange);
      localStorage.setItem = originalSetItem;
    };
  }, []);
  const {
    run: debouncedSetPredictionQuery
  } = useDebounceFn((value_0: string) => {
    setPredictionQuery(value_0);
  }, {
    wait: 300
  });
  useEffect(() => {
    if (isFocused && searchValue) {
      debouncedSetPredictionQuery(searchValue);
    } else {
      setPredictionQuery("");
    }
  }, [searchValue, isFocused, debouncedSetPredictionQuery]);
  const {
    data: popularSearchesData
  } = useQuery({
    ...orpc.search.public_getHotWords.queryOptions(),
    staleTime: 0,
    gcTime: 0
  });
  const popularSearches: SuggestionItem[] = useMemo(() => {
    if (!popularSearchesData?.words || !Array.isArray(popularSearchesData.words)) {
      return [];
    }
    return popularSearchesData.words.map((word, index) => ({
      id: `popular-${index}`,
      label: String(word) // 确保 word 是字符串
    }));
  }, [popularSearchesData]);
  const {
    data: predictionData,
    isLoading: isLoadingPredictions,
    isFetching: isFetchingPredictions
  } = useQuery({
    ...orpc.search.public_prediction.queryOptions({
      input: {
        q: predictionQuery,
        n: 10
      }
    }),
    enabled: !!predictionQuery && isFocused,
    staleTime: 3 * 60 * 1000
  });
  const predictions: SuggestionItem[] = useMemo(() => {
    try {
      if (!predictionData?.items) {
        return [];
      }
      // 确保items是数组
      if (!Array.isArray(predictionData.items)) {
        return [];
      }
      return predictionData.items.map((item: any, index_0) => {
        // 确保必要的字段存在
        const itemId = item?.id ? String(item.id) : `unknown-${index_0}`;
        const itemName = item?.name ? String(item.name) : "";
        return {
          id: `pred-${index_0}-${itemId}`,
          label: itemName
        };
      }).filter(item_0 => item_0.label.length > 0); // 过滤掉没有label的项
    } catch (error_0) {
      console.error("处理预测数据时出错:", error_0);
      return [];
    }
  }, [predictionData]);
  useEffect(() => {
    let intervalId: NodeJS.Timeout | null = null;
    if (popularSearches && popularSearches.length > 0) {
      setCurrentPlaceholder(popularSearches[0].label);
      setPlaceholderIndex(0);
      if (popularSearches.length > 1) {
        intervalId = setInterval(() => {
          setPlaceholderIndex(prevIndex => {
            const nextIndex = (prevIndex + 1) % popularSearches.length;
            setCurrentPlaceholder(popularSearches[nextIndex].label);
            return nextIndex;
          });
        }, 4000);
      }
    } else {
      setCurrentPlaceholder("");
    }
    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [popularSearches]);
  const handleFocus = () => {
    setIsFocused(true);
    document.body.style.overflow = "hidden";
  };
  const closeSearchPanel = useCallback(() => {
    setIsFocused(false);
    if (document.activeElement === searchInputRef.current) {
      searchInputRef.current?.blur();
    }
    document.body.style.overflow = "";
  }, []);
  const handleSearchSubmit = (searchTerm: string, searchAction: "input_text" | "history_search" | "hot_search" = "input_text") => {
    const term = searchTerm.trim();
    if (!term) return;

    // 先添加到历史记录
    addHistory(term);
    setSearchValue("");

    // 先关闭搜索面板
    closeSearchPanel();

    // 使用setTimeout确保在DOM更新后再进行导航操作，延长时间确保面板关闭完成
    setTimeout(() => {
      // 导航到搜索页面
      router.push(`/search/${encodeURIComponent(term)}`);

      // GTM 搜索埋点
      trackSearch({
        search_term: term
      });
      track("PopMartGlobalWebSearch", {
        keyword: term,
        search_action: searchAction
      }, TrackingEventType.CLICK);
    }, 0);
  };
  const handleKeyDown = (event_0: React.KeyboardEvent<HTMLInputElement>) => {
    if (event_0.key === "Enter") {
      if (searchValue.trim()) {
        handleSearchSubmit(searchValue, "input_text");
      }
    }
    if (event_0.key === "Escape") {
      closeSearchPanel();
    }
  };
  const handleSuggestionClick = (label: string, type: "history_search" | "hot_search") => {
    handleSearchSubmit(label, type);
  };
  const _clearSearchHistory = () => {
    clearHistory();
  };
  const defaultSearchView = <div className="grid grid-cols-2 gap-8 pb-6">
			<div>
				<h3 className="mb-3 border-b border-[#eee] pb-1 font-[DINPro-Medium] text-xs text-[#999]">
					{t("search.popularSearches")}
				</h3>
				<div className="flex flex-col space-y-4">
					{popularSearches && popularSearches.length > 0 && popularSearches.slice(0, 8).map(item_1 => <button type="button" key={item_1.id} onClick={e_0 => {
          e_0.stopPropagation();
          e_0.preventDefault();
          // 直接使用setTimeout确保事件处理完成后再执行搜索
          setTimeout(() => {
            handleSuggestionClick(item_1.label, "hot_search");
          }, 10);
        }} className="cursor-pointer block truncate rounded-md text-left text-sm font-normal leading-none text-black transition-colors hover:text-[#c01e29]">
								{item_1.label}
							</button>)}
				</div>
			</div>

			<div>
				<div className="mb-4 flex items-center justify-between border-b border-[#eee] pb-1">
					<h3 className="font-[DINPro-Medium] text-xs text-[#999]">
						{t("search.history")}
					</h3>
					{localSearchHistory.length > 0 && <div onClick={e_1 => {
          e_1.stopPropagation();
          clearHistory();
        }} className="text-gray-400 transition-colors hover:text-gray-600">
							<DeleteIcon className="h-3 w-3" />
						</div>}
				</div>
				<div className="flex flex-wrap gap-2">
					{localSearchHistory && localSearchHistory.length > 0 && localSearchHistory.slice(0, 5).map(item_2 => <div key={item_2.id} onClick={e_2 => {
          e_2.stopPropagation();
          e_2.preventDefault();
          // 直接使用setTimeout确保事件处理完成后再执行搜索
          setTimeout(() => {
            handleSuggestionClick(item_2.label, "history_search");
          }, 10);
        }} className="block truncate rounded-md bg-[#F5F5F5] px-2.5 py-1 text-sm font-normal leading-none text-black transition-colors hover:text-[#c01e29]">
								{item_2.label}
							</div>)}
				</div>
			</div>
		</div>;
  return <search ref={searchContainerRef} className={cn("relative mx-auto max-w-md", className)}>
			<div className="relative flex items-center">
				<Input id="header-search-input" name="search" autoComplete="off" placeholder={currentPlaceholder} className="block truncate rounded-full border border-black pl-4 pr-8 font-normal" style={{
        width: "164px",
        height: "32px",
        fontSize: "14px",
        backgroundColor: "white"
      }} value={searchValue} onChange={e_3 => setSearchValue(e_3.target.value)} onFocus={handleFocus} onKeyDown={handleKeyDown} ref={searchInputRef} aria-label={t("search.searchPlaceholder", "搜索商品")} aria-haspopup="listbox" aria-expanded={isFocused} aria-controls="search-panel-content" maxLength={50} />
				<button type="button" className="absolute right-2 top-1/2 -translate-y-1/2 cursor-pointer" onClick={e_4 => {
        e_4.stopPropagation();
        handleSearchSubmit(searchValue || currentPlaceholder);
      }} aria-label={t("search.submitSearch", "提交搜索")}>
					<Image src={searchIcon} width={24} height={24} className="text-gray-600" alt="" aria-hidden="true" unoptimized />
				</button>
			</div>

			{typeof window !== "undefined" && createPortal(<AnimatePresence>
						{isFocused && <React.Fragment>
								<motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 0.5
        }} exit={{
          opacity: 0
        }} transition={{
          duration: 0.3
        }} className="fixed inset-0 bg-black bg-opacity-30 z-9998" onClick={closeSearchPanel} aria-hidden="true" />
								<motion.div id="search-panel-content" role="listbox" initial={{
          y: -20,
          opacity: 0
        }} animate={{
          y: 0,
          opacity: 1
        }} exit={{
          y: -20,
          opacity: 0
        }} transition={{
          duration: 0.3,
          ease: "easeOut"
        }} className="fixed left-0 right-0 top-0 z-9999 bg-white shadow-md"
        // 阻止事件冒泡到背景层
        onClick={e_5 => e_5.stopPropagation()}>
									<div className="mx-auto max-w-[700px] py-6 text-sm font-normal leading-none text-black">
										<div className="mb-6 border-b border-black pb-1">
											<div className="flex items-center justify-between">
												<Input id="search-panel-input" name="search-panel" autoComplete="off" type="text" placeholder="" className="flex-1 border-none pl-1 text-base shadow-none focus-visible:ring-0 focus-visible:ring-offset-0" style={{
                  height: "28px",
                  backgroundColor: "white"
                }} value={searchValue} onChange={e_6 => setSearchValue(e_6.target.value)} onKeyDown={handleKeyDown} autoFocus aria-label={t("search.searchInPanel")} maxLength={50} />
												<div onClick={e_7 => {
                  e_7.stopPropagation();
                  searchValue.trim() && handleSearchSubmit(searchValue, "input_text");
                }} className="p-1 text-gray-500 hover:text-black">
													<Image src={searchIcon} width={24} height={24} alt="搜索" unoptimized />
												</div>
											</div>
										</div>

										{predictionQuery ? isLoadingPredictions || isFetchingPredictions ? <div className="flex items-center justify-center py-4 text-gray-500">
													<Loader2 className="mr-2 h-4 w-4 animate-spin" />
													<span>{t("search.searchLoading")}</span>
												</div> : predictions.length > 0 ? <div className="pb-4">
													<ul className="space-y-1">
														{predictions.map(item_3 => <li key={item_3.id}>
																<div onClick={e_8 => {
                    e_8.stopPropagation();
                    e_8.preventDefault();
                    // 直接使用setTimeout确保事件处理完成后再执行搜索
                    setTimeout(() => {
                      handleSuggestionClick(item_3.label, "hot_search");
                    }, 10);
                  }} className="w-full rounded-md p-2 text-left text-sm font-normal leading-none text-black transition-colors hover:bg-gray-100">
																	<span className="block truncate">
																		{item_3.label}
																	</span>
																</div>
															</li>)}
													</ul>
												</div> : defaultSearchView : defaultSearchView}
									</div>
								</motion.div>
							</React.Fragment>}
					</AnimatePresence>, document.body)}
		</search>;
};